// File: src/context/DragDropContext.jsx
import React, { createContext, useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import authService from '../services/authService'; // Assuming authService handles API calls

const DragDropContext = createContext({
  draggedResource: null,
  isDragging: false,
  dragOverTarget: null,
  setDraggedResource: () => {},
  setDragOverTarget: () => {},
  startDragging: () => {},
  endDragging: () => {},
  moveResource: async () => {},
  orphanResource: async () => {} // Added orphanResource to context definition
});

/**
 * Provider for drag and drop operations on resources
 */
export const DragDropProvider = ({ children }) => {
  const [draggedResource, setDraggedResource] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOverTarget, setDragOverTarget] = useState(null);

  const startDragging = useCallback((resource) => {
    console.log("Start Dragging:", resource);
    setDraggedResource(resource);
    setIsDragging(true);
  }, []);

  const endDragging = useCallback(() => {
    console.log("End Dragging");
    setDraggedResource(null);
    setIsDragging(false);
    setDragOverTarget(null);
  }, []);

  /**
   * Make a resource an orphan by removing its parent reference.
   * @param {Object} childResource - The resource to orphan.
   * @param {Object} oldParentResource - The old parent resource (e.g., the unit).
   * @returns {Promise<boolean>} - Success status.
   */
  const orphanResource = useCallback(async (childResource, oldParentResource) => {
    if (!childResource || !oldParentResource) {
      console.error("Invalid orphan operation: Missing required resources", { childResource, oldParentResource });
      return false;
    }

    // --- START FIX ---
    // Determine the correct parent_type for the API call.
    // If the UI type is 'unit' or 'collection', use 'collection' for the API.
    let apiParentType = oldParentResource.type || 'collection'; // Default to collection
    if (apiParentType === 'unit') {
        apiParentType = 'collection';
        console.log(`Mapping parent type 'unit' to 'collection' for API call.`);
    }
    // --- END FIX ---

    const url = '/api/v1/references/manage'; // Assuming this is the correct endpoint
    console.log(`Orphaning resource ${childResource.id} from parent ${oldParentResource.id} (API Parent Type: ${apiParentType})`);

    try {
      const actions = [{
        action: "remove",
        parent_id: oldParentResource.id,
        // --- Use the corrected API parent type ---
        parent_type: apiParentType,
        // --- End Use ---
        child_id: childResource.id,
        child_type: childResource.type || 'unknown' // Ensure child type is also sent correctly
      }];

      console.log("Sending orphan request payload:", JSON.stringify({ actions }));

      // Use authService or your API client to make the call
      const response = await authService.authFetch(url, {
        method: 'POST',
        body: { actions } // Ensure body is correctly formatted if authFetch stringifies
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error(`Failed to orphan resource: ${response.status}`, errorData);
        throw new Error(errorData.detail || `Failed to update references: ${response.status}`);
      }

      const result = await response.json();
      console.log("Orphan API response:", result);

      // Check if the specific 'remove' action succeeded in the results array
      const removeActionResult = result.results?.find(r =>
          r.action === 'remove' &&
          r.parent_id === oldParentResource.id &&
          r.child_id === childResource.id
      );

      if (removeActionResult?.success) {
          console.log("Orphan successful according to API response.");
          return true;
      } else {
          console.warn("Orphan failed according to API response:", removeActionResult?.message || "No success flag found.");
          // Throw an error based on the message if available
          throw new Error(removeActionResult?.message || "Orphan operation reported failure.");
      }

    } catch (error) {
      console.error('Error orphaning resource:', error);
      // Consider tracking network errors if authService provides it
      // authService.trackNetworkError('orphanResource', error);
      return false;
    }
  }, []); // Added dependencies if needed, e.g., authService if it's from context

  /**
   * Move a resource from one parent to another
   * @param {Object} childResource - The resource being moved
   * @param {Object} newParentResource - The new parent resource (null to make orphan)
   * @param {Object} oldParentResource - The old parent resource (optional)
   * @returns {Promise<boolean>} - Success status
   */
  const moveResource = useCallback(async (childResource, newParentResource, oldParentResource = null) => {
    if (!childResource) {
      console.error("Invalid resource move operation: Missing child resource");
      return false;
    }

    // If dropping onto the orphan zone (represented by newParentResource being null/specific target)
    if (!newParentResource && oldParentResource) {
      console.log("Move target is orphan zone, calling orphanResource...");
      return orphanResource(childResource, oldParentResource);
    }

    // If moving to a valid new parent
    if (!newParentResource || !newParentResource.id) {
      console.error("Invalid resource move operation: Missing or invalid new parent resource");
      return false;
    }

    // --- START FIX for moveResource ---
    let apiOldParentType = oldParentResource?.type || 'collection';
    if (apiOldParentType === 'unit') apiOldParentType = 'collection';

    let apiNewParentType = newParentResource.type || 'collection';
    if (apiNewParentType === 'unit') apiNewParentType = 'collection';
    // --- END FIX for moveResource ---

    const url = '/api/v1/references/manage';
    console.log(`Moving resource ${childResource.id} from parent ${oldParentResource?.id} (API Type: ${apiOldParentType}) to parent ${newParentResource.id} (API Type: ${apiNewParentType})`);

    try {
      const actions = [];

      // Action to remove from old parent (if applicable)
      if (oldParentResource && oldParentResource.id) {
        actions.push({
          action: "remove",
          parent_id: oldParentResource.id,
          parent_type: apiOldParentType, // Use corrected type
          child_id: childResource.id,
          child_type: childResource.type || 'unknown'
        });
      }

      // Action to add to new parent
      actions.push({
        action: "add",
        parent_id: newParentResource.id,
        parent_type: apiNewParentType, // Use corrected type
        child_id: childResource.id,
        child_type: childResource.type || 'unknown',
        metadata: null // Add metadata if needed for ordering, etc.
      });

      console.log("Sending move request payload:", JSON.stringify({ actions }));

      const response = await authService.authFetch(url, {
        method: 'POST',
        body: { actions }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error(`Failed to move resource: ${response.status}`, errorData);
        throw new Error(errorData.detail || `Failed to update references: ${response.status}`);
      }

      const result = await response.json();
      console.log("Move API response:", result);

      // Check if *all* actions in the batch succeeded
      const allSucceeded = result.results?.every(actionResult => actionResult.success);

      if (allSucceeded) {
          console.log("Move successful according to API response.");
          return true;
      } else {
          const failedAction = result.results?.find(r => !r.success);
          console.warn("Move failed according to API response:", failedAction?.message || "At least one action failed.");
          throw new Error(failedAction?.message || "Move operation reported failure.");
      }

    } catch (error) {
      console.error('Error updating resource references:', error);
      // authService.trackNetworkError('moveResource', error);
      return false;
    }
  }, [orphanResource]); // Added orphanResource dependency

  const contextValue = {
    draggedResource,
    isDragging,
    dragOverTarget,
    setDraggedResource,
    setDragOverTarget,
    startDragging,
    endDragging,
    moveResource,
    orphanResource // Expose orphanResource
  };

  return (
    <DragDropContext.Provider value={contextValue}>
      {children}
    </DragDropContext.Provider>
  );
};

DragDropProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const useDragDrop = () => useContext(DragDropContext);

export default DragDropContext;